<template>
    <div class="mx-2 mx-lg-5">
        <div class="card mt-3">
            <div class="card-header">
                <h3 class="mb-0">{{ $t("evacuation.title") }}</h3>
            </div>
            <div class="card-body">
                <div class="d-flex flex-wrap gap-2 mb-4">
                    <button class="btn btn-lg btn-primary fw-bold" :disabled="!selected.length || downloading" @click="download">
                        <span v-if="downloading" class="spinner-border spinner-border-sm me-1"></span>
                        {{ $t("evacuation.download") }}
                    </button>
                    <button
                        class="btn btn-lg btn-danger fw-bold"
                        :disabled="!selected.length || sending || !selectedRecipients.length"
                        @click="send"
                    >
                        <span v-if="sending" class="spinner-border spinner-border-sm me-1"></span>
                        {{ $t("evacuation.send") }}
                    </button>
                    <button class="btn btn-lg btn-outline-secondary" :disabled="loading" @click="load">
                        {{ $t("evacuation.refresh") }}
                    </button>
                </div>

                <div class="d-flex flex-wrap gap-4 mb-3">
                    <div v-for="category in availableCategories" :key="category" class="form-check">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            :id="`category_${category}`"
                            :value="category"
                            v-model="selected"
                            @change="load"
                        >
                        <label class="form-check-label fw-bold" :for="`category_${category}`">
                            {{ $t(`evacuation.categories.${category}`) }}
                        </label>
                    </div>
                </div>

                <div v-if="loaded" class="mb-3">
                    <div class="fw-bold mb-1">{{ $t("evacuation.recipients") }}</div>
                    <div class="d-flex flex-wrap gap-3">
                        <div v-for="email in recipients" :key="email" class="form-check">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                :id="`recipient_${email}`"
                                :value="email"
                                v-model="selectedRecipients"
                            >
                            <label class="form-check-label" :for="`recipient_${email}`">{{ email }}</label>
                        </div>
                    </div>
                    <form v-if="mayAddRecipients" class="d-flex gap-2 mt-2" style="max-width: 32rem" @submit.prevent="addRecipient">
                        <input
                            id="evacuationExtraEmail"
                            v-model="extraEmail"
                            type="email"
                            class="form-control"
                            :placeholder="$t('evacuation.addRecipientPlaceholder')"
                        >
                        <button type="submit" class="btn btn-outline-secondary text-nowrap" :disabled="!extraEmail.trim()">
                            {{ $t("evacuation.addRecipient") }}
                        </button>
                    </form>
                    <p v-else-if="!recipients.length" class="text-muted small mb-0">{{ $t("evacuation.noRecipients") }}</p>
                    <p v-if="mayAddRecipients" class="text-muted small mt-1 mb-0">{{ $t("evacuation.oneTimeHint") }}</p>
                </div>

                <div v-if="message" class="alert" :class="message.error ? 'alert-danger' : 'alert-success'">
                    {{ message.text }}
                </div>

                <div v-if="oneTime" class="alert alert-info">
                    <div>{{ $t("evacuation.oneTimePassword", { emails: oneTime.emails.join(", ") }) }}</div>
                    <div class="fs-3 fw-bold font-monospace my-1" id="evacuationOneTimePassword">{{ oneTime.password }}</div>
                    <div class="small">{{ $t("evacuation.oneTimePasswordNote") }}</div>
                </div>

                <div v-if="loaded && !selected.length" class="alert alert-secondary">{{ $t("evacuation.selectCategory") }}</div>

                <template v-else-if="list">
                    <div class="alert alert-warning">
                        <div class="fw-bold fs-5">{{ list.gatehouse }}</div>
                        <div>{{ $t("evacuation.generatedAt") }}: {{ $d(new Date(list.generated_at), 'long') }}</div>
                        <div class="fs-5 mt-1">
                            {{ $t("evacuation.peopleCount") }}: <b>{{ list.people_count }}</b>
                            <span class="mx-2">·</span>
                            {{ $t("evacuation.vehicleCount") }}: <b>{{ list.vehicle_count }}</b>
                        </div>
                        <div v-if="list.long_stay_count" class="mt-2">* {{ $t("evacuation.longStay") }}</div>
                    </div>

                    <template v-if="list.categories.includes('guestbooks')">
                        <h5 class="mt-4">{{ $t("evacuation.categories.guestbooks") }} ({{ list.guestbooks.length }})</h5>
                        <div v-if="list.guestbooks.length" class="table-responsive">
                            <table class="table table-striped">
                                <thead>
                                    <tr>
                                        <th>{{ $t("guestbook.firstName") }}</th>
                                        <th>{{ $t("guestbook.lastName") }}</th>
                                        <th>{{ $t("guestbook.company") }}</th>
                                        <th>{{ $t("guestbook.phone") }}</th>
                                        <th>{{ $t("guestbook.from") }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="guest in list.guestbooks" :key="guest.id" :class="{ 'table-warning': guest.long_stay }">
                                        <td>{{ guest.first_name || "—" }}</td>
                                        <td>{{ guest.last_name }}</td>
                                        <td>{{ guest.company || "—" }}</td>
                                        <td>
                                            <a v-if="guest.phone" :href="`tel:${guest.phone}`">{{ guest.phone }}</a>
                                            <span v-else>—</span>
                                        </td>
                                        <td>{{ guest.actual_from ? $d(new Date(guest.actual_from), 'short') : "—" }}{{ guest.long_stay ? " *" : "" }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p v-else class="text-muted">{{ $t("evacuation.empty.guestbooks") }}</p>
                    </template>

                    <template v-if="list.categories.includes('vehicle_entries')">
                        <h5 class="mt-4">
                            {{ $t("evacuation.categories.vehicle_entries") }} ({{ list.vehicle_entries.length }})
                        </h5>
                        <div v-if="list.vehicle_entries.length" class="table-responsive">
                            <table class="table table-striped">
                                <thead>
                                    <tr>
                                        <th>{{ $t("vehicleEntry.licencePlate") }}</th>
                                        <th>{{ $t("vehicleEntry.carrier") }}</th>
                                        <th>{{ $t("vehicleEntry.driver_name") }}</th>
                                        <th>{{ $t("vehicleEntry.driverPhone") }}</th>
                                        <th>{{ $t("vehicleEntry.from") }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="vehicle in list.vehicle_entries" :key="vehicle.id" :class="{ 'table-warning': vehicle.long_stay }">
                                        <td>{{ vehicle.licence_plate }}</td>
                                        <td>{{ vehicle.carrier }}</td>
                                        <td>{{ vehicle.driver_name }}</td>
                                        <td>
                                            <a v-if="vehicle.driver_phone" :href="`tel:${vehicle.driver_phone}`">{{ vehicle.driver_phone }}</a>
                                            <span v-else>—</span>
                                        </td>
                                        <td>{{ vehicle.actual_from ? $d(new Date(vehicle.actual_from), 'short') : "—" }}{{ vehicle.long_stay ? " *" : "" }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p v-else class="text-muted">{{ $t("evacuation.empty.vehicle_entries") }}</p>
                    </template>

                    <template v-if="list.categories.includes('vehicle_nfc')">
                        <h5 class="mt-4">{{ $t("evacuation.categories.vehicle_nfc") }} ({{ list.vehicle_nfc.length }})</h5>
                        <div v-if="list.vehicle_nfc.length" class="table-responsive">
                            <table class="table table-striped">
                                <thead>
                                    <tr>
                                        <th>{{ $t("vehicleNfcEntry.licencePlate") }}</th>
                                        <th>{{ $t("evacuation.vehicle") }}</th>
                                        <th>{{ $t("vehicleNfcEntry.arrived") }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="entry in list.vehicle_nfc" :key="entry.id" :class="{ 'table-warning': entry.long_stay }">
                                        <td>{{ entry.licence_plate_snapshot || "—" }}</td>
                                        <td>{{ entry.name_snapshot || "—" }}</td>
                                        <td>{{ $d(new Date(entry.arrived_at), 'short') }}{{ entry.long_stay ? " *" : "" }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p v-else class="text-muted">{{ $t("evacuation.empty.vehicle_nfc") }}</p>
                    </template>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            list: null,
            availableCategories: [],
            selected: [],
            recipients: [],
            selectedRecipients: [],
            mayAddRecipients: false,
            extraEmail: "",
            oneTime: null,
            loaded: false,
            loading: false,
            downloading: false,
            sending: false,
            message: null,
        }
    },
    methods: {
        categoriesParam() {
            // before the first load nothing is selected yet and the server returns every category
            return this.loaded ? { categories: this.selected.join(",") } : {};
        },
        load() {
            // any 403 sends the user back to the login, a gatehouse without the list must be told here
            if (this.$store.getters.selectedGatehouse?.settings && !this.$store.getters.canViewEvacuationList) {
                this.message = { error: true, text: this.$t("evacuation.notAllowed") };
                return;
            }
            if (this.loaded && !this.selected.length) return;

            this.loading = true;
            const slug = this.$route.params.slug;
            const requested = this.selected.join(",");
            this.$store.getters.api.get(`/${slug}/evacuation-list`, { params: this.categoriesParam() })
            .then(response => {
                // a slower response for another gatehouse or since changed categories must not overwrite the list
                if (slug !== this.$route.params.slug || (this.loaded && requested !== this.selected.join(","))) return;
                this.list = response.data;
                this.availableCategories = response.data.available_categories;
                this.mayAddRecipients = response.data.may_add_recipients;
                if (!this.loaded) {
                    this.recipients = [...response.data.recipients];
                    this.selectedRecipients = [...response.data.recipients];
                    this.selected = [...response.data.categories];
                    this.loaded = true;
                }
            })
            .catch(error => {
                console.error("Error loading evacuation list", error);
                this.message = { error: true, text: this.$t(error.response?.status === 403 ? "evacuation.notAllowed" : "evacuation.loadError") };
            })
            .finally(() => {
                this.loading = false;
            });
        },
        download() {
            this.downloading = true;
            this.$store.getters.api.get(`/${this.$route.params.slug}/evacuation-list/pdf`, {
                params: { categories: this.selected.join(",") },
                responseType: "blob",
            })
            .then(response => {
                const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `evakuacni_seznam_${this.$route.params.slug}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            })
            .catch(error => {
                console.error("Error downloading evacuation list", error);
                this.message = { error: true, text: this.$t("evacuation.downloadError") };
            })
            .finally(() => {
                this.downloading = false;
            });
        },
        addRecipient() {
            const email = this.extraEmail.trim().toLowerCase();
            if (!email) return;
            if (!this.recipients.includes(email)) this.recipients.push(email);
            if (!this.selectedRecipients.includes(email)) this.selectedRecipients.push(email);
            this.extraEmail = "";
        },
        send() {
            const emails = [...this.selectedRecipients];
            if (!window.confirm(this.$t("evacuation.sendConfirm", { emails: emails.join(", ") }))) return;

            this.sending = true;
            this.message = null;
            this.oneTime = null;
            this.$store.getters.api.post(`/${this.$route.params.slug}/evacuation-list/send`, {
                categories: this.selected,
                emails,
            })
            .then(response => {
                const { recipients, failed } = response.data;
                let text = this.$t("evacuation.sent", { emails: recipients.join(", ") });
                if (failed.length) text += " " + this.$t("evacuation.failed", { emails: failed.join(", ") });
                this.message = { error: failed.length > 0, text };
                if (response.data.one_time_password) {
                    this.oneTime = {
                        password: response.data.one_time_password,
                        emails: response.data.one_time_password_recipients,
                    };
                }
            })
            .catch(error => {
                console.error("Error sending evacuation list", error);
                const code = error.response?.data?.error_code;
                const key = code === 1205 ? "evacuation.invalidRecipients" : "evacuation.sendError";
                this.message = { error: true, text: this.$t(key) };
            })
            .finally(() => {
                this.sending = false;
            });
        },
    },
    watch: {
        "$route.params.slug": {
            immediate: true,
            handler() {
                this.loaded = false;
                this.list = null;
                this.selected = [];
                this.recipients = [];
                this.selectedRecipients = [];
                this.extraEmail = "";
                this.message = null;
                this.oneTime = null;
                this.load();
            }
        }
    }
}
</script>
